import { Phone } from 'lucide-react';
import { Fragment, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { ClickToCallDialog } from '@apps/web/components/voice_calls/click_to_call_dialog';
import { useDebounce } from '@apps/web/hooks/use_debounce';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import {
    useOrganization,
    useOrganizationCaregivers,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

type SelectedCaregiver = {
    readonly id: string;
    readonly name: string;
};

// Icon button for toolbars (e.g. the conversations list header) that opens a
// caregiver picker and starts a click-to-call. Renders nothing unless the
// organization has click_to_call_enabled, so callers need no flag check.
export function ClickToCallLauncher() {
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'click-to-call-launcher',
    });
    const clickToCallEnabled = useFeatureFlag(
        'click_to_call_enabled',
        organization?.features,
    );
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search.trim(), 300);
    const [selectedCaregiver, setSelectedCaregiver] =
        useState<SelectedCaregiver | null>(null);
    const { data: caregivers, isLoading: isLoadingCaregivers } =
        useOrganizationCaregivers(
            isPickerOpen ? organizationId : null,
            { reason: 'click-to-call-launcher' },
            { search: debouncedSearch, per_page: 25 },
        );

    if (!clickToCallEnabled || !organizationId) {
        return null;
    }

    return (
        <Fragment>
            <Tooltip>
                <TooltipTrigger asChild={true}>
                    <Button
                        aria-label="Call a caregiver"
                        className="h-11 w-11 shrink-0 shadow-none md:h-8 md:w-8"
                        onClick={() => {
                            setSearch('');
                            setIsPickerOpen(true);
                        }}
                        size="icon"
                        type="button"
                        variant="ghost"
                    >
                        <Phone className="h-4 w-4" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>Call a caregiver</TooltipContent>
            </Tooltip>
            <Dialog open={isPickerOpen} onOpenChange={setIsPickerOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Call a caregiver</DialogTitle>
                        <DialogDescription>
                            Choose who to call from your organization’s number.
                        </DialogDescription>
                    </DialogHeader>
                    <Input
                        autoFocus={true}
                        placeholder="Search caregivers by name"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                    <ul className="max-h-72 divide-y overflow-y-auto rounded-md border">
                        {isLoadingCaregivers ? (
                            <li className="text-muted-foreground px-4 py-6 text-center text-sm">
                                Loading…
                            </li>
                        ) : (
                            (caregivers ?? []).map((caregiver) => (
                                <li key={caregiver.id}>
                                    <button
                                        className="hover:bg-accent flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left"
                                        onClick={() => {
                                            setIsPickerOpen(false);
                                            setSelectedCaregiver({
                                                id: caregiver.id,
                                                name: caregiver.name,
                                            });
                                        }}
                                        type="button"
                                    >
                                        <span className="min-w-0">
                                            <span className="block truncate font-medium">
                                                {caregiver.name}
                                            </span>
                                            {caregiver.office_name ? (
                                                <span className="text-muted-foreground block truncate text-sm">
                                                    {caregiver.office_name}
                                                </span>
                                            ) : null}
                                        </span>
                                        <Phone className="text-muted-foreground h-4 w-4 shrink-0" />
                                    </button>
                                </li>
                            ))
                        )}
                        {!isLoadingCaregivers &&
                        (caregivers ?? []).length === 0 ? (
                            <li className="text-muted-foreground px-4 py-6 text-center text-sm">
                                No caregivers found.
                            </li>
                        ) : null}
                    </ul>
                </DialogContent>
            </Dialog>
            {selectedCaregiver ? (
                <ClickToCallDialog
                    organizationId={organizationId}
                    caregiverId={selectedCaregiver.id}
                    caregiverName={selectedCaregiver.name}
                    open={true}
                    onOpenChange={(open) => {
                        if (!open) {
                            setSelectedCaregiver(null);
                        }
                    }}
                />
            ) : null}
        </Fragment>
    );
}
